import { cn } from "@/lib/utils";
import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
} from "@/components/ui/Pagination";

type Props = {
	itemsPerPage: number;
	currentPage: number;
	totalRows: number;
	onPageChange: (page: number) => void;
};

const MAX_VISIBLE_PAGES = 10;

const getVisiblePages = (
	currentPage: number,
	totalPages: number,
): (number | "ellipsis")[] => {
	if (totalPages <= MAX_VISIBLE_PAGES) {
		return Array.from({ length: totalPages }, (_, index) => index + 1);
	}

	const pages: (number | "ellipsis")[] = [1];
	const start = Math.max(2, currentPage - 1);
	const end = Math.min(totalPages - 1, currentPage + 1);

	if (start > 2) pages.push("ellipsis");

	for (let page = start; page <= end; page++) {
		pages.push(page);
	}

	if (end < totalPages - 1) pages.push("ellipsis");
	pages.push(totalPages);

	return pages;
};

const TransactionPagination = ({
	itemsPerPage,
	currentPage,
	totalRows,
	onPageChange,
}: Props) => {
	const totalPages = Math.max(1, Math.ceil(totalRows / itemsPerPage));
	const visiblePages = getVisiblePages(currentPage, totalPages);
	const rangeStart = totalRows === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
	const rangeEnd = Math.min(currentPage * itemsPerPage, totalRows);

	const handlePageChange = (page: number) => (event: React.MouseEvent) => {
		event.preventDefault();
		if (page < 1 || page > totalPages) return;
		onPageChange(page);
	};

	return (
		<div className="flex w-full flex-col gap-4 py-4 md:flex-row md:items-center md:justify-between md:py-3">
			<div className="text-center text-sm font-medium text-textDark md:text-left">
				{rangeStart} - {rangeEnd} of {totalRows}
			</div>

			{totalPages > 1 && (
				<Pagination className="mx-0 w-full justify-center md:w-auto md:justify-end">
					<PaginationContent>
						<PaginationItem>
							<PaginationPrevious
								href="#"
								onClick={handlePageChange(currentPage - 1)}
								className={cn(
									"px-2 sm:pl-2.5 [&>span]:hidden sm:[&>span]:inline",
									currentPage === 1 && "pointer-events-none opacity-50",
								)}
								aria-disabled={currentPage === 1}
							/>
						</PaginationItem>

						{visiblePages.map((page, index) =>
							page === "ellipsis" ? (
								<PaginationItem
									key={`ellipsis-${visiblePages[index - 1]}-${visiblePages[index + 1]}`}
								>
									<PaginationEllipsis />
								</PaginationItem>
							) : (
								<PaginationItem key={page}>
									<PaginationLink
										href="#"
										isActive={currentPage === page}
										onClick={handlePageChange(page)}
										className={cn(
											currentPage === page &&
												"border-primary-600 bg-primary-600 text-white hover:bg-primary-600/90 hover:text-white",
										)}
									>
										{page}
									</PaginationLink>
								</PaginationItem>
							),
						)}

						<PaginationItem>
							<PaginationNext
								href="#"
								onClick={handlePageChange(currentPage + 1)}
								className={cn(
									"px-2 sm:pr-2.5 [&>span:first-child]:hidden sm:[&>span:first-child]:inline",
									currentPage === totalPages &&
										"pointer-events-none opacity-50",
								)}
								aria-disabled={currentPage === totalPages}
							/>
						</PaginationItem>
					</PaginationContent>
				</Pagination>
			)}
		</div>
	);
};

export default TransactionPagination;
